/**
 * Interactive satellite map with a draggable pin for fine-tuning the
 * property coordinates. Uses Google Maps JS API loaded lazily via
 * `loadGoogleMaps()`, key fetched once per session from the tenant settings.
 *
 * Behaviour:
 *  - Drag the pin → onChange(newLat, newLon)
 *  - External lat/lon prop change (e.g. user clicks "Geocodificar") →
 *    recenters map + moves marker
 *  - Single click on map → moves marker to that point (also fires onChange)
 *  - Loading/error states fall back to the original Google Maps embed
 *    iframe, preserving the previous read-only experience
 *
 * Types: we use `any` for the google namespace to avoid adding
 * @types/google.maps as a tenant dep. The surface is small (Map, Marker,
 * a couple of events) so the trade-off is fine — if we grow uses,
 * promote to typed.
 */

import { useEffect, useRef, useState } from "react"
import { Loader2, MapPinned } from "lucide-react"
import type { AppShell } from "@/App"
import { getGoogleMapsConfig } from "@/lib/api"
import { loadGoogleMaps } from "@/lib/google-maps"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GMaps = any

interface DraggableSatelliteMapProps {
  shell: AppShell | null
  latitude: number
  longitude: number
  /** Fired when the user drags the pin or clicks elsewhere. */
  onChange: (lat: number, lon: number) => void
  zoom?: number
  className?: string
}

export function DraggableSatelliteMap({
  shell,
  latitude,
  longitude,
  onChange,
  zoom = 19,
  className = "h-72",
}: DraggableSatelliteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<GMaps | null>(null)
  const markerRef = useRef<GMaps | null>(null)
  const onChangeRef = useRef(onChange)
  const [status, setStatus] = useState<"loading" | "ready" | "fallback">("loading")
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Keep callback ref fresh without re-running map init effect.
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // One-shot: fetch the API key + load the JS API + create the map.
  useEffect(() => {
    let cancelled = false
    async function init() {
      if (!shell) return
      try {
        const cfg = await getGoogleMapsConfig(shell)
        if (cancelled) return
        if (!cfg.enabled || !cfg.apiKey) {
          setStatus("fallback")
          setErrorMsg(
            "Google Maps não configurada — usa as coordenadas manuais ou activa em Definições do CRM.",
          )
          return
        }
        const google = (await loadGoogleMaps(cfg.apiKey)) as GMaps
        if (cancelled || !containerRef.current) return

        // With `loading=async` in the script URL, the Maps API uses dynamic
        // library loading: `google.maps.Map` etc. are NOT immediately
        // available — they must be requested via `importLibrary()`. This is
        // the new recommended pattern (Google deprecated the synchronous
        // constructor access in 2024); the old `new google.maps.Map(...)`
        // pattern crashes with "undefined is not a constructor".
        const [{ Map }, { Marker }] = await Promise.all([
          google.maps.importLibrary("maps") as Promise<GMaps>,
          google.maps.importLibrary("marker") as Promise<GMaps>,
        ])
        if (cancelled || !containerRef.current) return

        const center = { lat: latitude, lng: longitude }
        const map = new Map(containerRef.current, {
          center,
          zoom,
          mapTypeId: "hybrid", // satellite + roads/labels overlay
          tilt: 0,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
        })
        const marker = new Marker({
          position: center,
          map,
          draggable: true,
          title: "Arraste para ajustar a localização exacta",
        })
        marker.addListener("dragend", (e: { latLng: { lat: () => number; lng: () => number } }) => {
          const lat = e.latLng.lat()
          const lng = e.latLng.lng()
          onChangeRef.current(lat, lng)
        })
        map.addListener(
          "click",
          (e: { latLng: { lat: () => number; lng: () => number } }) => {
            const lat = e.latLng.lat()
            const lng = e.latLng.lng()
            marker.setPosition({ lat, lng })
            onChangeRef.current(lat, lng)
          },
        )
        mapRef.current = map
        markerRef.current = marker
        setStatus("ready")
      } catch (err) {
        if (cancelled) return
        setStatus("fallback")
        setErrorMsg(
          err instanceof Error ? err.message : "Falha a carregar Google Maps",
        )
      }
    }
    init()
    return () => {
      cancelled = true
    }
    // Intentionally only on shell: we don't want a remount when coords
    // change (the imperative effect below moves the marker instead).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell])

  // External coordinate updates → recentre + move marker (imperative).
  // Skipped when the marker IS the source (handled by `onChange` callback
  // which updates parent state which re-renders us → we'd loop without a
  // diff check, but Google's setPosition is idempotent at sub-meter so
  // the loop terminates naturally).
  useEffect(() => {
    if (status !== "ready" || !mapRef.current || !markerRef.current) return
    const pos = { lat: latitude, lng: longitude }
    markerRef.current.setPosition(pos)
    // Only recenter when significantly different (e.g. geocoded jump)
    const center = mapRef.current.getCenter()
    if (!center) return
    const dist =
      Math.abs(center.lat() - latitude) + Math.abs(center.lng() - longitude)
    if (dist > 0.0005) {
      mapRef.current.setCenter(pos)
    }
  }, [latitude, longitude, status])

  if (status === "fallback") {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="overflow-hidden rounded-md border border-border h-full">
          <iframe
            title="Mapa satellite (fallback)"
            className="h-full w-full"
            src={`https://www.google.com/maps?q=${latitude},${longitude}&z=18&t=k&output=embed`}
          />
        </div>
        {errorMsg ? (
          <p className="text-xs text-muted-foreground">{errorMsg}</p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className={`overflow-hidden rounded-md border border-border ${className} w-full`}
      />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          A carregar mapa…
        </div>
      )}
      {status === "ready" && (
        <div className="pointer-events-none absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-md bg-background/85 px-2 py-1 text-xs text-muted-foreground shadow-sm">
          <MapPinned className="h-3 w-3" />
          Arrasta o pin para afinar
        </div>
      )}
    </div>
  )
}
